import { useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Printer, FileText } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import {
  CURRENCY_SYMBOL,
  EGYPT_VAT_RATE,
  breakdownInclusive,
  formatCurrency,
} from "@/lib/currency";

export interface SubscriptionInvoiceData {
  id: string;
  nursery_name: string;
  nursery_address?: string | null;
  nursery_tax_number?: string | null;
  plan_label: string;
  billing_cycle?: string | null;
  pricing_model?: string | null;
  children_count?: number | null;
  price_per_child?: number | null;
  month: number;
  year: number;
  amount: number; // VAT-inclusive total
  vat_rate?: number | null;
  status: string;
  due_date: string;
  paid_at?: string | null;
  created_at: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  invoice: SubscriptionInvoiceData | null;
  issuerName?: string; // company name on top
  issuerLogoUrl?: string;
}

const MONTHS_AR = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

const SubscriptionInvoiceDialog = ({
  open,
  onOpenChange,
  invoice,
  issuerName = "NurseryNet — منصة إدارة الحضانات",
  issuerLogoUrl,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  if (!invoice) return null;

  const vatRate = Number(invoice.vat_rate ?? EGYPT_VAT_RATE);
  const totals = breakdownInclusive(Number(invoice.amount || 0), vatRate);
  const invoiceNo = `SUB-${invoice.year}${String(invoice.month).padStart(2, "0")}-${invoice.id.slice(0, 6).toUpperCase()}`;

  const lineDescription =
    invoice.pricing_model === "per_child"
      ? `اشتراك ${invoice.plan_label} • ${invoice.children_count || 0} طفل × ${formatCurrency(invoice.price_per_child || 0, { withSymbol: false })} ${CURRENCY_SYMBOL}`
      : `اشتراك ${invoice.plan_label} (${invoice.billing_cycle === "yearly" ? "سنوي" : "شهري"})`;

  const handlePrint = () => window.print();

  const handleDownload = async () => {
    if (!ref.current) return;
    try {
      toast.loading("جاري إنشاء PDF...", { id: "pdf" });
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");
      const canvas = await html2canvas(ref.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, "PNG", 0, 0, w, h);
      pdf.save(`${invoiceNo}.pdf`);
      toast.success("تم تنزيل الفاتورة", { id: "pdf" });
    } catch (e: any) {
      toast.error(e?.message || "فشل التحميل", { id: "pdf" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            فاتورة اشتراك مفصّلة
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-end gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={handlePrint} className="gap-1">
            <Printer className="w-4 h-4" /> طباعة
          </Button>
          <Button size="sm" onClick={handleDownload} className="gap-1">
            <Download className="w-4 h-4" /> تنزيل PDF
          </Button>
        </div>

        {/* Printable area */}
        <div
          ref={ref}
          className="bg-white text-black p-8 rounded-lg border"
          style={{ fontFamily: "Tajawal, Cairo, Arial, sans-serif" }}
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b-2 border-primary pb-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-primary">{issuerName}</h2>
              <p className="text-xs text-gray-500 mt-1">جمهورية مصر العربية</p>
            </div>
            <div className="text-left">
              {issuerLogoUrl && (
                <img src={issuerLogoUrl} alt="logo" className="h-12 mb-2 ml-auto" />
              )}
              <p className="text-xs text-gray-500">رقم الفاتورة</p>
              <p className="font-mono font-bold text-sm">{invoiceNo}</p>
            </div>
          </div>

          {/* Meta */}
          <div className="grid grid-cols-2 gap-4 text-sm mb-6">
            <div>
              <p className="text-xs text-gray-500 mb-1">فاتورة إلى</p>
              <p className="font-bold text-base">{invoice.nursery_name}</p>
              {invoice.nursery_address && (
                <p className="text-xs text-gray-600">{invoice.nursery_address}</p>
              )}
              {invoice.nursery_tax_number && (
                <p className="text-xs text-gray-600 mt-1">
                  رقم تسجيل ضريبي: <span className="font-mono">{invoice.nursery_tax_number}</span>
                </p>
              )}
            </div>
            <div className="text-left">
              <div className="flex justify-end gap-3 text-xs">
                <span className="text-gray-500">تاريخ الإصدار:</span>
                <span className="font-medium">
                  {format(new Date(invoice.created_at), "dd MMM yyyy", { locale: ar })}
                </span>
              </div>
              <div className="flex justify-end gap-3 text-xs mt-1">
                <span className="text-gray-500">تاريخ الاستحقاق:</span>
                <span className="font-medium">
                  {format(new Date(invoice.due_date), "dd MMM yyyy", { locale: ar })}
                </span>
              </div>
              <div className="flex justify-end gap-3 text-xs mt-1">
                <span className="text-gray-500">عن شهر:</span>
                <span className="font-medium">
                  {MONTHS_AR[invoice.month - 1]} {invoice.year}
                </span>
              </div>
              <div className="flex justify-end gap-3 mt-2">
                <Badge
                  className={
                    invoice.status === "paid"
                      ? "bg-green-100 text-green-800 border-green-300"
                      : "bg-amber-100 text-amber-800 border-amber-300"
                  }
                >
                  {invoice.status === "paid" ? "✓ مدفوعة" : "غير مدفوعة"}
                </Badge>
              </div>
            </div>
          </div>

          {/* Items table */}
          <table className="w-full text-sm border-collapse mb-6">
            <thead>
              <tr className="bg-primary/10 text-primary">
                <th className="p-3 text-right border border-primary/20 w-12">#</th>
                <th className="p-3 text-right border border-primary/20">البيان</th>
                <th className="p-3 text-center border border-primary/20 w-20">الكمية</th>
                <th className="p-3 text-left border border-primary/20 w-32">الإجمالي</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-3 border border-gray-200 text-center">1</td>
                <td className="p-3 border border-gray-200">{lineDescription}</td>
                <td className="p-3 border border-gray-200 text-center">
                  {invoice.pricing_model === "per_child" ? (invoice.children_count || 0) : 1}
                </td>
                <td className="p-3 border border-gray-200 text-left font-medium">
                  {formatCurrency(totals.subtotal)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-72 space-y-2 text-sm">
              <div className="flex justify-between p-2">
                <span className="text-gray-600">المجموع الفرعي</span>
                <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between p-2 bg-gray-50 rounded">
                <span className="text-gray-600">ضريبة القيمة المضافة ({vatRate}%)</span>
                <span className="font-medium">{formatCurrency(totals.vatAmount)}</span>
              </div>
              <div className="flex justify-between p-3 bg-primary text-white rounded font-bold text-base">
                <span>الإجمالي المستحق</span>
                <span>{formatCurrency(totals.total)}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-4 border-t text-xs text-gray-500 space-y-1">
            <p>• الأسعار شاملة ضريبة القيمة المضافة المصرية {vatRate}%.</p>
            <p>• يُرجى سداد المبلغ في موعد أقصاه تاريخ الاستحقاق المذكور أعلاه.</p>
            <p>• هذه الفاتورة صادرة إلكترونياً ولا تحتاج توقيع.</p>
            {invoice.paid_at && (
              <p className="text-green-700 font-medium">
                ✓ تم السداد بتاريخ: {format(new Date(invoice.paid_at), "dd MMM yyyy", { locale: ar })}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionInvoiceDialog;
